/* HUENIT LAB 2.0 — Admin: 공유 관리(모더레이션).
   사용자(선생님)가 공개 공유한 코드를 열람·수정(제목/설명)·삭제(공개 내림)한다.
   목록/삭제/수정은 repo.js(public/code/* 객체 + Lambda 자동 재머지). 코드 자체(블록)는
   편집하지 않는다 — 제목/설명 순화 또는 삭제로 대응. */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Icon } from '../../icons/Icon.jsx';
import { Modal, Confirm, useToast } from '../../components/index.js';
import { useSession } from '../../auth/session.jsx';
import { fmtDateTime } from '../../lib/datetime.js';
import { downloadObject } from '../../lib/download.js';
import { listShares, updateShareMeta, deleteShare } from './repo.js';

function KindBadge({ kind }) {
  const py = kind === 'pythonCode';
  return <span className={"ex-card__kind ex-card__kind--" + (py ? 'python' : 'block')} style={{ fontSize: 11 }}>
    <Icon name={py ? 'python' : 'blocks'} size={12} />{py ? 'Python' : '블록'}
  </span>;
}

export function SharesAdmin() {
  const toast = useToast();
  const { mode, getCredentials } = useSession();

  const [items, setItems] = useState(null);     // null=로딩 | []=빈 | [..]
  const [error, setError] = useState(null);
  const [q, setQ] = useState('');
  const [editing, setEditing] = useState(null);  // share
  const [draft, setDraft] = useState({ title: '', description: '' });
  const [saving, setSaving] = useState(false);
  const [del, setDel] = useState(null);          // share
  const [delBusy, setDelBusy] = useState(false);

  const load = useCallback(async () => {
    setError(null); setItems(null);
    try { setItems(await listShares()); }
    catch (e) { setError(e && e.message ? e.message : String(e)); setItems([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openEdit = (s) => { setDraft({ title: s.title || '', description: s.description || '' }); setEditing(s); };

  const saveMeta = async () => {
    setSaving(true);
    try {
      const updated = await updateShareMeta(getCredentials, editing, { title: draft.title, description: draft.description });
      setItems(prev => (prev || []).map(x => x.id === editing.id ? { ...x, title: updated.title, description: updated.description } : x));
      toast(mode === 'fake' ? '공유글을 수정했습니다' : '수정했습니다 — 앱 반영까지 몇 초 걸릴 수 있습니다');
      setEditing(null);
    } catch (e) {
      toast('수정 실패: ' + (e && e.message ? e.message : e), 'error');
    } finally { setSaving(false); }
  };

  const remove = async () => {
    setDelBusy(true);
    try {
      await deleteShare(getCredentials, del);
      setItems(prev => (prev || []).filter(x => x.id !== del.id));   // 낙관적 반영
      toast(mode === 'fake' ? '공유글을 삭제했습니다' : '삭제했습니다 — 앱 갤러리 반영까지 몇 초 걸릴 수 있습니다');
      setDel(null);
    } catch (e) {
      toast('삭제 실패: ' + (e && e.message ? e.message : e), 'error');
    } finally { setDelBusy(false); }
  };

  const download = async (s) => {
    if (mode === 'fake') { toast('데모(오프라인) 모드에서는 실제 파일 다운로드를 지원하지 않습니다.'); return; }
    try { await downloadObject(s.codeUrl, `${s.title || s.id}.${s.kind === 'pythonCode' ? 'py' : 'huenit'}`); }
    catch (e) { toast('다운로드 실패: ' + (e && e.message ? e.message : e), 'error'); }
  };

  const shown = useMemo(() => {
    const term = q.trim().toLowerCase();
    const list = items || [];
    if (!term) return list;
    return list.filter(s => (s.title || '').toLowerCase().includes(term) || (s.ownerEmail || '').toLowerCase().includes(term));
  }, [items, q]);

  if (items === null) return <div className="empty-state"><div className="empty-state__t">공유 목록을 불러오는 중…</div></div>;
  if (error) {
    return (
      <div className="empty-state">
        <div className="empty-state__icon"><Icon name="help" size={26} /></div>
        <div className="empty-state__t">공유 목록을 불러오지 못했습니다</div>
        <div className="empty-state__d">{error}</div>
        <div style={{ marginTop: 16 }}><button className="btn btn--ghost" onClick={load}><Icon name="refresh" size={15} />다시 시도</button></div>
      </div>
    );
  }

  return (
    <div>
      <div className="toolbar">
        <div className="search">
          <Icon name="open" size={16} />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="제목 또는 작성자 이메일 검색" />
        </div>
        <div style={{ fontSize: 12.5, color: 'var(--ink-400)', fontWeight: 500 }}>{(items || []).length}건</div>
        <button className="btn btn--ghost btn--sm" onClick={load} title="새로고침"><Icon name="refresh" size={15} />새로고침</button>
      </div>

      {shown.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state__icon"><Icon name="open" size={26} /></div>
          <div className="empty-state__t">{(items || []).length === 0 ? '공유된 게시물이 없습니다' : '조건에 맞는 공유글이 없습니다'}</div>
          <div className="empty-state__d">{(items || []).length === 0 ? '사용자가 코드를 공개로 공유하면 여기에 표시됩니다.' : '검색어를 바꿔 보세요.'}</div>
        </div>
      ) : (
        <div className="rows">
          {shown.map(s => (
            <div className="row" key={s.id} style={{ alignItems: 'flex-start' }}>
              {s.thumbnail
                ? <img src={s.thumbnail} alt="" onError={e => { e.currentTarget.style.display = 'none'; }} style={{ width: 46, height: 46, borderRadius: 9, objectFit: 'cover', flexShrink: 0, border: '1px solid var(--ink-200)' }} />
                : <span style={{ width: 46, height: 46, borderRadius: 9, display: 'grid', placeItems: 'center', background: 'var(--ink-100)', color: 'var(--ink-400)', flexShrink: 0 }}><Icon name="code" size={20} /></span>}
              <div className="row__main">
                <div className="row__title">{s.title || <span style={{ color: 'var(--ink-400)' }}>(제목 없음)</span>}<KindBadge kind={s.kind} /></div>
                <div className="row__sub" style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <span className="mono">{s.ownerEmail || '(작성자 미상)'}</span>
                  <span>공유 {fmtDateTime(s.uploadedAt)}</span>
                </div>
                {s.description && <div style={{ marginTop: 8, padding: '8px 11px', background: 'var(--ink-50)', border: '1px solid var(--ink-150)', borderRadius: 9, fontSize: 12.5, color: 'var(--ink-600)', lineHeight: 1.5 }}>{s.description}</div>}
              </div>
              <div className="row__actions" style={{ paddingTop: 2 }}>
                <button className="icon-btn" onClick={() => download(s)} title="코드 다운로드"><Icon name="download" size={15} /></button>
                <button className="btn btn--ghost btn--sm" onClick={() => openEdit(s)}>수정</button>
                <button className="btn btn--danger btn--sm" onClick={() => setDel(s)}>삭제</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <Modal title="공유글 수정" sub="부적절한 제목·설명을 순화합니다. 코드 자체는 편집하지 않습니다."
          onClose={saving ? () => {} : () => setEditing(null)}
          foot={<>
            <button className="btn btn--ghost" onClick={() => setEditing(null)} disabled={saving}>취소</button>
            <button className="btn btn--primary" onClick={saveMeta} disabled={saving || !draft.title.trim()}>{saving ? '저장 중…' : '저장'}</button>
          </>}>
          <div className="field">
            <label className="field__label">제목<span className="req">*</span></label>
            <input className="input" value={draft.title} onChange={e => setDraft({ ...draft, title: e.target.value })} placeholder="공유글 제목" />
          </div>
          <div className="field">
            <label className="field__label">설명</label>
            <textarea className="textarea" value={draft.description} onChange={e => setDraft({ ...draft, description: e.target.value })} placeholder="공유글 설명" />
          </div>
          <div className="field__hint">작성자: <b>{editing.ownerEmail || '(미상)'}</b> · 저장 시 공개 메타(display_name/description)만 갱신됩니다.</div>
        </Modal>
      )}

      {del && <Confirm title="이 공유글을 삭제할까요?"
        body={<><b>{del.title || del.id}</b> 공개 게시물을 삭제합니다(공개 갤러리에서 제거). 작성자의 개인 저장소 원본은 유지됩니다. 앱 반영까지 몇 초 걸릴 수 있습니다.</>}
        confirmLabel={delBusy ? '삭제 중…' : '삭제'}
        onConfirm={delBusy ? () => {} : remove} onClose={delBusy ? () => {} : () => setDel(null)} />}
    </div>
  );
}

export default SharesAdmin;
